/**
 * Unified AI Service - Hybrid Groq + Local LLM System
 * Routes tasks intelligently between Groq (cloud) and local Ollama model
 */

import { groqService } from './groqService'
import { ollamaService } from './ollamaService'
import { aiContextManager } from './aiContextManager'

const AI_CONFIG = {
  groqEnabled: import.meta.env.VITE_GROQ_ENABLED !== 'false',
  ollamaEnabled: import.meta.env.VITE_OLLAMA_ENABLED !== 'false',
  ollamaUrl: import.meta.env.VITE_OLLAMA_URL || 'http://localhost:11434',
  groqApiKey: import.meta.env.VITE_GROQ_API_KEY,
  defaultModel: 'ollama', // Use local Ollama by default (Deepseek)
  fallbackToOllama: true,
  contextSize: 5000, // Max context to store
}

export const aiService = {
  /**
   * Process analysis tasks (use Groq for heavy computation)
   */
  async analyzeMetrics(data, context = '') {
    try {
      // Store context for learning
      aiContextManager.addContext('analysis', data, context)

      if (AI_CONFIG.groqEnabled) {
        return await groqService.analyzeData(data, context)
      } else if (AI_CONFIG.ollamaEnabled) {
        return await ollamaService.analyzeData(data, context)
      }
      throw new Error('No AI service available')
    } catch (error) {
      console.error('Analysis failed:', error)
      return this._fallbackResponse('analysis', data)
    }
  },

  /**
   * Generate insights (use Groq for intelligence)
   */
  async generateInsights(metrics, category = 'general') {
    try {
      aiContextManager.addContext('insights', metrics, category)

      if (AI_CONFIG.groqEnabled) {
        return await groqService.generateInsights(metrics, category)
      } else {
        return await ollamaService.generateInsights(metrics, category)
      }
    } catch (error) {
      console.error('Insight generation failed:', error)
      return this._fallbackResponse('insights', metrics)
    }
  },

  /**
   * Generate recommendations (use Groq for smart recommendations)
   */
  async generateRecommendations(situation, context = {}) {
    try {
      aiContextManager.addContext('recommendations', situation, context)

      if (AI_CONFIG.groqEnabled) {
        return await groqService.generateRecommendations(situation, context)
      } else {
        return await ollamaService.generateRecommendations(situation, context)
      }
    } catch (error) {
      console.error('Recommendation generation failed:', error)
      return []
    }
  },

  /**
   * Voice/Persona response (use LOCAL Ollama for personalization)
   * This is where your AI personality comes through
   */
  async generateVoiceResponse(userInput, personaContext = {}) {
    try {
      // Always prefer local Ollama for voice to maintain persona
      aiContextManager.addContext('voice', userInput, personaContext)

      if (AI_CONFIG.ollamaEnabled) {
        return await ollamaService.generateVoiceResponse(
          userInput,
          personaContext,
          aiContextManager.getRecentContext('voice')
        )
      } else if (AI_CONFIG.groqEnabled) {
        // Fallback to Groq if Ollama unavailable
        return await groqService.generateVoiceResponse(userInput, personaContext)
      }
      throw new Error('No voice AI service available')
    } catch (error) {
      console.error('Voice generation failed:', error)
      return "I'm experiencing a temporary issue. Please try again."
    }
  },

  /**
   * Adaptive response based on learned patterns
   */
  async generateAdaptiveResponse(userMessage, userId = 'default') {
    try {
      // Get user's learned patterns
      const userPattern = aiContextManager.getUserPattern(userId)

      // Use local Ollama for personalized adaptive responses
      if (AI_CONFIG.ollamaEnabled) {
        return await ollamaService.generateAdaptiveResponse(
          userMessage,
          userPattern,
          aiContextManager.getRecentContext('adaptive', userId)
        )
      }

      // Fallback to Groq
      return await groqService.generateAdaptiveResponse(userMessage, userPattern)
    } catch (error) {
      console.error('Adaptive response failed:', error)
      return null
    }
  },

  /**
   * Process alerts (use Groq for critical analysis)
   */
  async processAlert(alertData, severity = 'medium') {
    try {
      aiContextManager.addContext('alerts', alertData, severity)

      if (AI_CONFIG.groqEnabled) {
        return await groqService.processAlert(alertData, severity)
      } else {
        return await ollamaService.processAlert(alertData, severity)
      }
    } catch (error) {
      console.error('Alert processing failed:', error)
      return { action: 'log', priority: severity }
    }
  },

  /**
   * Learn from user feedback and interactions
   */
  async learnFromFeedback(interaction, feedback) {
    try {
      // Store for fine-tuning dataset
      aiContextManager.recordFeedback(interaction, feedback)

      // If using local Ollama, optionally fine-tune
      if (AI_CONFIG.ollamaEnabled && feedback.shouldFineTune) {
        await ollamaService.recordTrainingData(interaction, feedback)
      }

      return { success: true, learning: true }
    } catch (error) {
      console.error('Learning from feedback failed:', error)
      return { success: false, learning: false }
    }
  },

  /**
   * Get system status
   */
  async getSystemStatus() {
    const status = {
      groq: { enabled: AI_CONFIG.groqEnabled, available: false },
      ollama: { enabled: AI_CONFIG.ollamaEnabled, available: false },
      timestamp: new Date().toISOString(),
    }

    // Check Groq availability
    if (AI_CONFIG.groqEnabled) {
      try {
        await groqService.ping()
        status.groq.available = true
      } catch (e) {
        status.groq.available = false
      }
    }

    // Check Ollama availability
    if (AI_CONFIG.ollamaEnabled) {
      try {
        await ollamaService.ping()
        status.ollama.available = true
      } catch (e) {
        status.ollama.available = false
      }
    }

    // Learning status
    status.contextSize = aiContextManager.getContextSize()
    status.feedbackCount = aiContextManager.getFeedbackCount()
    status.userProfiles = aiContextManager.getUserProfileCount()

    return status
  },

  /**
   * Configure AI services
   */
  configureServices(config) {
    if (config.groqApiKey) {
      AI_CONFIG.groqApiKey = config.groqApiKey
      groqService.setApiKey(config.groqApiKey)
    }
    if (config.ollamaUrl) {
      AI_CONFIG.ollamaUrl = config.ollamaUrl
      ollamaService.setBaseUrl(config.ollamaUrl)
    }
    if (config.defaultModel) {
      AI_CONFIG.defaultModel = config.defaultModel
    }
    if (config.groqEnabled !== undefined) {
      AI_CONFIG.groqEnabled = config.groqEnabled
    }
    if (config.ollamaEnabled !== undefined) {
      AI_CONFIG.ollamaEnabled = config.ollamaEnabled
    }
  },

  /**
   * Get AI configuration
   */
  getConfig() {
    return {
      ...AI_CONFIG,
      groqApiKey: AI_CONFIG.groqApiKey ? '***configured***' : 'not configured',
    }
  },

  /**
   * Fallback responses when services unavailable
   */
  _fallbackResponse(type, data) {
    const fallbacks = {
      analysis: { status: 'offline', message: 'Analysis service temporarily unavailable' },
      insights: [
        { type: 'neutral', text: 'AI services are currently offline. Basic analysis unavailable.' },
      ],
      voice: 'I apologize, but I cannot respond at this moment. My AI systems are temporarily unavailable.',
    }
    return fallbacks[type] || { error: 'Service unavailable' }
  },

  /**
   * Clear learning data (privacy control)
   */
  clearLearningData() {
    aiContextManager.clearAllContext()
    if (AI_CONFIG.ollamaEnabled) {
      ollamaService.clearTrainingData()
    }
    return { cleared: true }
  },
}

export default aiService
